import type { TranscriptionMessage } from '@/store/slices/transcriptionSlice';
import { isAgentParticipant, serverMintedRole } from '@/lib/participantUtils';

/**
 * Canonical transcript shape persisted by both live sync and final archive.
 *
 * Optional Redux fields are represented as explicit nulls here. This keeps
 * `false`, `0`, an empty string, and a removed value distinguishable when we
 * fingerprint a segment or send a metadata-only correction.
 */
export interface PersistedTranscriptItem {
  sync_id: string;
  speaker_name: string;
  message: string;
  timestamp_ms: number;
  is_final: boolean;
  participant_identity: string | null;
  is_self: boolean;
  is_officer: boolean | null;
  color: string;
  initial: string;
  segment_id: string | null;
  segment_seq: number | null;
  segment_key: string | null;
  bubble_id: string | null;
  audio_start_time: number | null;
  audio_end_time: number | null;
  /** Rule 57 rescue mark; null for a provider final. Mirrors the backend writer. */
  rescue: 'flush' | 'tail' | null;
}

/** Full camelCase payload used by archive producers without dropping metadata. */
export interface ArchiveTranscriptItem {
  syncId: string;
  name: string;
  message: string;
  timestamp: number;
  isFinal: boolean;
  isSelf: boolean;
  isOfficer: boolean | null;
  color: string;
  initial: string;
  participantIdentity: string | null;
  segmentId: string | null;
  segmentSeq: number | null;
  segmentKey: string | null;
  bubbleId: string | null;
  audioStartTime: number | null;
  audioEndTime: number | null;
  /** Rule 57 rescue mark; the archive is a third writer of the row and must carry it. */
  rescue: 'flush' | 'tail' | null;
}

export type PersistedTranscriptInput = Partial<PersistedTranscriptItem> & {
  speaker_name?: string | null;
  message?: string | null;
  timestamp_ms?: number | null;
};

type TranscriptIdentityInput = PersistedTranscriptInput | TranscriptionMessage;

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function identityPart(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Return the immutable segment identity used by the delta protocol.
 *
 * Backend segment IDs are deterministic across tabs. Truly keyless legacy
 * rows use stable persisted fields plus their original ordinal; once this ID
 * is assigned it is written to `sync_id` and must be preserved on updates.
 */
export function transcriptSyncIdentity(
  item: TranscriptIdentityInput,
  index = 0,
): string {
  const record = item as unknown as Record<string, unknown>;
  const existing = nonEmptyString(record.sync_id) ?? nonEmptyString(record.syncId);
  if (existing) return existing;

  const participant =
    nonEmptyString(record.participant_identity) ??
    nonEmptyString(record.participantIdentity) ??
    nonEmptyString(record.speaker_name) ??
    nonEmptyString(record.name) ??
    'unknown';
  const segmentId = nonEmptyString(record.segment_id) ?? nonEmptyString(record.segmentId);
  if (segmentId) {
    return `segment-id:v1:${identityPart(participant)}:${identityPart(segmentId)}`;
  }
  const segmentKey = nonEmptyString(record.segment_key) ?? nonEmptyString(record.segmentKey);
  if (segmentKey) {
    return `segment-key:v1:${identityPart(participant)}:${identityPart(segmentKey)}`;
  }

  const audioStart =
    finiteNumberOrNull(record.audio_start_time) ??
    finiteNumberOrNull(record.audioStartTime);
  const timestamp =
    finiteNumberOrNull(record.timestamp_ms) ??
    finiteNumberOrNull(record.timestamp) ??
    0;
  const stableTime = audioStart === null ? `timestamp:${timestamp}` : `audio:${audioStart}`;
  const safeIndex = Number.isSafeInteger(index) && index >= 0 ? index : 0;

  return `legacy:v1:${identityPart(participant)}:${stableTime}:${safeIndex}`;
}

/**
 * The role a persisted row records (Q-153 r8, audit B33). The client's `isOfficer` is a claim: a server-minted suffix
 * decides for a browser seat; a phone leg or any other reserved identity is never the officer; the claim is honoured
 * only for an agent identity (officer-assist, whose flag the transcriber attests; full server-side attestation is
 * Q-843); a row with no identity keeps its flag (nothing to derive from).
 */
function derivedIsOfficer(identity: string | null | undefined, claimed: boolean | null | undefined): boolean | null {
  if (!identity) return claimed ?? null;
  const minted = serverMintedRole(identity);
  if (minted) return minted === 'officer';
  if (isAgentParticipant(identity)) return claimed ?? null;
  return false;
}

function persistedIsOfficer(message: TranscriptionMessage): boolean | null {
  return derivedIsOfficer(message.participantIdentity, message.isOfficer);
}

/**
 * The same rule for a segment ALREADY in persisted shape (`participant_identity`, `is_officer`, `is_self`), for writers
 * that receive persisted segments from a browser (app/api/transcripts/sync). Only a segment that names its identity is
 * touched: its `is_officer` is always (re)written from the identity (it may become `false` or `null` even when the client
 * sent none), and `is_self` only when present. One without an identity is legacy and passes as sent (Q-153 r8).
 */
export function attestPersistedSegmentRole<T extends Record<string, unknown>>(segment: T): T {
  const identity = segment.participant_identity;
  if (typeof identity !== 'string' || !identity) return segment;
  const claimed = typeof segment.is_officer === 'boolean' ? segment.is_officer : null;
  const isOfficer = derivedIsOfficer(identity, claimed);
  const out: Record<string, unknown> = { ...segment, is_officer: isOfficer };
  if ('is_self' in segment) out.is_self = segment.is_self === true && isOfficer === true;
  return out as T;
}

/** Convert a Redux segment to the one canonical persisted representation. */
export function toPersistedTranscript(
  message: TranscriptionMessage,
  index = 0,
): PersistedTranscriptItem {
  return {
    sync_id: transcriptSyncIdentity(message, index),
    speaker_name: message.name,
    message: message.message,
    timestamp_ms: message.timestamp,
    is_final: message.isFinal !== false,
    participant_identity: message.participantIdentity ?? null,
    // `is_self` marks the archiving browser's own rows, and that browser is the officer's: never a non-officer row.
    is_self: message.isSelf && persistedIsOfficer(message) === true,
    is_officer: persistedIsOfficer(message),
    color: message.color,
    initial: message.initial,
    segment_id: message.segmentId ?? null,
    segment_seq: finiteNumberOrNull(message.segmentSeq),
    segment_key: message.segmentKey ?? null,
    bubble_id: message.bubbleId ?? null,
    audio_start_time: finiteNumberOrNull(message.audioStartTime),
    audio_end_time: finiteNumberOrNull(message.audioEndTime),
    rescue: rescueMarkOrNull(message.rescue),
  };
}

/** Only the two marks the backend writes; anything else is not a rescue. */
export function rescueMarkOrNull(value: unknown): 'flush' | 'tail' | null {
  return value === 'flush' || value === 'tail' ? value : null;
}

/** Convert persisted/legacy DB content back to Redux without truthiness loss. */
export function fromPersistedTranscript(
  item: PersistedTranscriptInput,
  index = 0,
): TranscriptionMessage {
  const speakerName = item.speaker_name ?? '';
  return {
    syncId: transcriptSyncIdentity(item, index),
    name: speakerName,
    message: item.message ?? '',
    timestamp: item.timestamp_ms ?? 0,
    isFinal: item.is_final !== false,
    isSelf: item.is_self ?? false,
    isOfficer: item.is_officer ?? undefined,
    color: item.color ?? '#666',
    initial: item.initial ?? (speakerName.charAt(0).toUpperCase() || '?'),
    participantIdentity: item.participant_identity ?? undefined,
    segmentId: item.segment_id ?? undefined,
    segmentSeq: finiteNumberOrNull(item.segment_seq) ?? undefined,
    segmentKey: item.segment_key ?? undefined,
    bubbleId: item.bubble_id ?? undefined,
    audioStartTime: finiteNumberOrNull(item.audio_start_time) ?? undefined,
    audioEndTime: finiteNumberOrNull(item.audio_end_time) ?? undefined,
    rescue: rescueMarkOrNull(item.rescue) ?? undefined,
  };
}

/**
 * Collision-free client fingerprint of every persisted field.
 *
 * The object has a fixed property order and explicit nulls, so its canonical
 * JSON is safe to compare locally without retaining/serializing call history.
 */
export function transcriptFingerprint(item: PersistedTranscriptItem): string {
  return JSON.stringify(item);
}

/** Preserve every transcript field when crossing the archive API boundary. */
export function toArchiveTranscript(
  message: TranscriptionMessage,
  index = 0,
): ArchiveTranscriptItem {
  const persisted = toPersistedTranscript(message, index);
  return {
    syncId: persisted.sync_id,
    name: persisted.speaker_name,
    message: persisted.message,
    timestamp: persisted.timestamp_ms,
    isFinal: persisted.is_final,
    isSelf: persisted.is_self,
    isOfficer: persisted.is_officer,
    color: persisted.color,
    initial: persisted.initial,
    participantIdentity: persisted.participant_identity,
    segmentId: persisted.segment_id,
    segmentSeq: persisted.segment_seq,
    segmentKey: persisted.segment_key,
    bubbleId: persisted.bubble_id,
    audioStartTime: persisted.audio_start_time,
    audioEndTime: persisted.audio_end_time,
    rescue: persisted.rescue,
  };
}
